import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { formsApi } from '../api/formsApi';
import type { FormDetail, SubmissionDetail, SubmissionFile, SubmissionListItem } from '../api/types';

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Small line-icon wrapper (Feather-style, inherits colour via currentColor). */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const ViewIcon = () => (
  <Icon>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

const PdfIcon = () => (
  <Icon>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" />
    <polyline points="9 15 12 18 15 15" />
  </Icon>
);

/** Management view of a form's submitted responses: list + CSV export + per-row view/PDF. */
export function SubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<FormDetail | null>(null);
  const [rows, setRows] = useState<SubmissionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);

  const load = useCallback(() => {
    if (!id) {
      return;
    }
    setLoading(true);
    Promise.all([formsApi.get(id), formsApi.listSubmissions(id)])
      .then(([f, s]) => {
        setForm(f);
        setRows(s);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load submissions'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => load(), [load]);

  async function exportCsv() {
    if (!id || !form) {
      return;
    }
    setError(null);
    try {
      saveBlob(await formsApi.exportSubmissions(id), `${form.title}-submissions.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    }
  }

  async function openDetail(submissionId: string) {
    if (!id) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setDetail(await formsApi.getSubmission(id, submissionId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load submission');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(submissionId: string) {
    if (!id) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      saveBlob(await formsApi.submissionPdf(id, submissionId), `submission-${submissionId}.pdf`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF download failed');
    } finally {
      setBusy(false);
    }
  }

  async function downloadFile(submissionId: string, file: SubmissionFile) {
    if (!id) {
      return;
    }
    setError(null);
    try {
      saveBlob(await formsApi.submissionFile(id, submissionId, file.id), file.fileName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Attachment download failed');
    }
  }

  return (
    <div className="page">
      <div className="editor-header">
        <h1>{form ? `${form.title} — submissions` : 'Submissions'}</h1>
        <div className="actions">
          <button disabled={busy || rows.length === 0} onClick={exportCsv}>Export CSV</button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No submissions yet.</p>
      ) : (
        <table className="forms-table">
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Source</th>
              <th>Status</th>
              <th className="icon-col">View</th>
              <th className="icon-col">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{new Date(r.submittedAt ?? r.createdAt).toLocaleString()}</td>
                <td>{r.source}</td>
                <td>{r.status}</td>
                <td className="icon-col">
                  <button
                    className="icon-action"
                    title={r.hasFiles ? 'View response + attachments' : 'View response'}
                    aria-label="View response"
                    disabled={busy}
                    onClick={() => openDetail(r.id)}
                  >
                    <ViewIcon />
                  </button>
                </td>
                <td className="icon-col">
                  {r.hasPdf ? (
                    <button
                      className="icon-action"
                      title="Download PDF"
                      aria-label="Download PDF"
                      disabled={busy}
                      onClick={() => downloadPdf(r.id)}
                    >
                      <PdfIcon />
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Response — {new Date(detail.submittedAt ?? detail.createdAt).toLocaleString()}</h3>
              <button className="modal-close" aria-label="Close" onClick={() => setDetail(null)}>×</button>
            </div>
            <div className="modal-body">
              <table className="kv-table">
                <tbody>
                  {Object.entries(detail.data)
                    .filter(([k]) => k !== 'submit')
                    .map(([k, v]) => (
                      <tr key={k}>
                        <th>{detail.labels[k] ?? k}</th>
                        <td>{renderValue(v)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {detail.files.length > 0 && (
                <>
                  <h4>Attachments</h4>
                  <ul className="attach-list">
                    {detail.files.map((f) => (
                      <li key={f.id}>
                        <button className="link-btn" onClick={() => downloadFile(detail.id, f)}>
                          {f.fileName}
                        </button>
                        <span className="muted">
                          {' '}
                          ({formatSize(f.size)}
                          {f.scanStatus !== 'Clean' ? `, ${f.scanStatus}` : ''})
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
